import React, { useEffect, useMemo } from 'react';
import { Select, Alert, Row, Col, Card, Button, theme } from 'antd';
import {
  DashboardOutlined,
  ToolOutlined,
  DollarOutlined,
  HeartOutlined,
  TeamOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'libs/dayjs';
import { usePanelOperacional } from 'providers/PanelOperacionalProvider';
import { PageHeader, SectionHeader } from 'components/headings';
import { useTenantFlags } from 'hooks/useTenantFlags';
import ProLockOverlay from 'components/global/ProLock/ProLockOverlay';
import paths from 'assets/routes/paths';
import CumplimientoCronTile from './tiles/CumplimientoCronTile';
import BacklogTile from './tiles/BacklogTile';
import EmergenciasTile from './tiles/EmergenciasTile';
import ReportesEntregadosTile from './tiles/ReportesEntregadosTile';
import PipelineComercialTile from './tiles/PipelineComercialTile';
import AscensoresConflictivosTile from './tiles/AscensoresConflictivosTile';
import HallazgosCriticosTile from './tiles/HallazgosCriticosTile';
import SaludParqueItemTile from './tiles/SaludParqueItemTile';

const { useToken } = theme;

const buildMesOptions = () => {
  const now = dayjs();
  const opts = [];
  for (let i = 0; i < 12; i += 1) {
    const d = now.subtract(i, 'month');
    opts.push({ value: d.format('YYYY-MM'), label: d.format('MMMM YYYY') });
  }
  return opts;
};

function PanelOperacionalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useToken();
  const { isPro } = useTenantFlags();
  const mes = searchParams.get('mes') || dayjs().format('YYYY-MM');
  const { data, isLoading, error, call } = usePanelOperacional();
  const mesOptions = useMemo(buildMesOptions, []);

  useEffect(() => {
    if (!isPro) return;
    call({ queryParams: { mes } });
  }, [mes, call, isPro]);

  const setMes = (m) => setSearchParams({ mes: m });

  return (
    <ProLockOverlay
      locked={!isPro}
      title="Panel operacional"
      description="Tablero operativo en tiempo real de tus equipos y mantenciones. Disponible en Pro."
    >
      <div style={{ padding: token.paddingLG, paddingBottom: token.paddingXL }}>
        <PageHeader
          icon={DashboardOutlined}
          title="Panel Operacional"
          subtitle="Indicadores en vivo del negocio. Los datos se actualizan cada 5 minutos."
          actions={
            <Select
              value={mes}
              onChange={setMes}
              options={mesOptions}
              style={{ minWidth: 200 }}
              size="large"
              aria-label="Mes del panel"
            />
          }
        />

        {error && (
          <Alert
            type="error"
            message="No se pudo cargar el panel operacional"
            showIcon
            style={{ marginBottom: token.marginLG }}
          />
        )}

        <section style={{ marginBottom: token.marginXXL }}>
          <SectionHeader
            icon={ToolOutlined}
            tint={token.colorPrimary}
            title="Operacional"
            subtitle="¿El cron preventivo funciona? ¿Los técnicos están al día? ¿Hay solicitudes atoradas o emergencias?"
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} xl={6}>
              <CumplimientoCronTile data={data?.cron} loading={isLoading} />
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card title="Desempeño de técnicos" style={{ height: '100%' }}>
                <p
                  style={{
                    marginTop: 0,
                    marginBottom: token.marginMD,
                    color: token.colorTextSecondary,
                  }}
                >
                  Ranking comparativo de mantenciones, ODT, emergencias y solicitudes por técnico.
                </p>
                <Link to={paths.admin.tecnicos}>
                  <Button type="primary" icon={<TeamOutlined />} block>
                    Ver ranking completo <ArrowRightOutlined />
                  </Button>
                </Link>
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <BacklogTile data={data?.backlog} loading={isLoading} />
            </Col>
            <Col xs={24} md={12} xl={6}>
              <EmergenciasTile data={data?.emergencias} loading={isLoading} />
            </Col>
          </Row>
        </section>

        <section style={{ marginBottom: token.marginXXL }}>
          <SectionHeader
            icon={DollarOutlined}
            tint={token.colorWarning}
            title="Comercial"
            subtitle="¿Los reportes de mantención llegan al cliente? ¿Cuánto dinero hay en presupuestos esperando aprobación?"
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <ReportesEntregadosTile data={data?.reportes} loading={isLoading} />
            </Col>
            <Col xs={24} md={12}>
              <PipelineComercialTile data={data?.pipeline} loading={isLoading} />
            </Col>
          </Row>
        </section>

        <section>
          <SectionHeader
            icon={HeartOutlined}
            tint={token.colorError}
            title="Salud del parque"
            subtitle="¿Qué ascensores generan más problemas? ¿Cuántos hallazgos críticos siguen abiertos?"
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={12}>
              <AscensoresConflictivosTile
                data={data?.ascensores_conflictivos}
                loading={isLoading}
              />
            </Col>
            <Col xs={24} md={12} xl={6}>
              <HallazgosCriticosTile data={data?.hallazgos_criticos} loading={isLoading} />
            </Col>
            <Col xs={24} md={12} xl={6}>
              <SaludParqueItemTile data={data?.checklist_salud} loading={isLoading} />
            </Col>
          </Row>
        </section>
      </div>
    </ProLockOverlay>
  );
}

export default PanelOperacionalPage;
